// Thin wrapper around PostHog to keep components clean.
// Uses the global `window.posthog` initialized in /public/index.html.

export function track(event, props = {}) {
  try {
    if (typeof window !== "undefined" && window.posthog) {
      window.posthog.capture(event, props);
    }
  } catch (e) {
    // never break the UI over analytics
  }
}

export function identify(id, props = {}) {
  try {
    if (typeof window !== "undefined" && window.posthog && id) {
      window.posthog.identify(id, props);
    }
  } catch (e) {}
}

/**
 * Feature-flag helpers — PostHog cohort-based flags for A/B tests.
 * All three degrade gracefully to `false` / `null` when PostHog is offline,
 * so the UI never explodes and the fallback branch always ships.
 *
 * Usage:
 *   const isNewCart = useFeatureFlag("new-cart-experience");
 *   <div className={isNewCart ? "new" : "old"}>...
 */
import { useEffect, useState } from "react";

function _ph() {
  return (typeof window !== "undefined" && window.posthog) || null;
}

export function isFeatureEnabled(key, defaultValue = false) {
  try {
    const ph = _ph();
    if (!ph || typeof ph.isFeatureEnabled !== "function") return defaultValue;
    const v = ph.isFeatureEnabled(key);
    return typeof v === "boolean" ? v : defaultValue;
  } catch { return defaultValue; }
}

export function getFeatureFlag(key, defaultValue = null) {
  try {
    const ph = _ph();
    if (!ph || typeof ph.getFeatureFlag !== "function") return defaultValue;
    const v = ph.getFeatureFlag(key);
    return v === undefined ? defaultValue : v;
  } catch { return defaultValue; }
}

/**
 * React hook: reactive feature-flag value that updates when the flag
 * server pushes an update or the user identity changes.
 */
export function useFeatureFlag(key, defaultValue = false) {
  const [flag, setFlag] = useState(() => isFeatureEnabled(key, defaultValue));
  useEffect(() => {
    const ph = _ph();
    if (!ph) return;
    // Immediate refresh in case the flags loaded after mount.
    setFlag(isFeatureEnabled(key, defaultValue));
    // Subscribe if PostHog exposes onFeatureFlags (v1.115+).
    let off = null;
    try {
      if (typeof ph.onFeatureFlags === "function") {
        off = ph.onFeatureFlags(() => setFlag(isFeatureEnabled(key, defaultValue)));
      }
    } catch { /* noop */ }
    return () => { try { off && off(); } catch { /* noop */ } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return flag;
}

/**
 * A/B variant hook — returns the assigned variant string, or `defaultVariant`.
 * Also fires an $feature_flag_called event so PostHog knows the user was
 * bucketed (needed for accurate conversion attribution).
 */
export function useVariant(key, defaultVariant = "control") {
  const [variant, setVariant] = useState(defaultVariant);
  useEffect(() => {
    const ph = _ph();
    if (!ph) return;
    const compute = () => {
      const v = getFeatureFlag(key, defaultVariant);
      setVariant(typeof v === "string" ? v : defaultVariant);
      try { ph.capture("$feature_flag_called", { $feature_flag: key, $feature_flag_response: v }); } catch { /* noop */ }
    };
    compute();
    let off = null;
    try {
      if (typeof ph.onFeatureFlags === "function") off = ph.onFeatureFlags(compute);
    } catch { /* noop */ }
    return () => { try { off && off(); } catch { /* noop */ } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return variant;
}
